// {{WORKTREE_ROOT}}/picosponsorconsole/packages/design/src/icons.ts
// PLAN2 WP09 — C-122 / RL-58 / PLAN2 §4.5: one concept, one icon, one name, shared by the site and the console.
// PLAN6 WP6-04: module and agent concepts are MdOutline*, UI controls are plain Md*; the agent marks are AGENT_ICONS.
// Material icons from react-icons/md 5.3.0 only (no icon font: CSP font-src 'self'). Icons inherit
// currentColor and are aria-hidden wherever a visible word accompanies them.
import type { IconType } from "react-icons";
import {
  MdOutlineMap, MdOutlineHandshake, MdOutlineBolt, MdOutlineDescription, MdOutlineViewKanban, MdOutlineVerified,
  MdOutlineMeetingRoom, MdOutlineNotificationsActive, MdOutlineSummarize, MdOutlineInbox, MdOutlineInventory2,
  MdOutlineTravelExplore, MdOutlineWidgets, MdOutlineAutorenew, MdOutlinePeopleAlt, MdOutlineFilterAlt, MdOutlineMoveUp,
  MdOutlineMenuBook, MdOutlineHub, MdOutlineAssistant, MdOutlineGroups, MdOutlineStorefront, MdOutlineSettings,
  MdOutlinePhotoCamera, MdOutlineFactCheck, MdOutlineRadar, MdOutlineDriveFileMove, MdOutlineSchedule, MdOutlineVisibility,
  MdOutlineCheckBox, MdOutlineAssessment, MdOutlineSupportAgent, MdOutlineDiamond, MdArrowDropDown,
  MdArrowForward, MdArrowBack, MdArrowUpward, MdArrowDownward, MdExpandMore, MdLockOutline, MdClose, MdCheck, MdAdd,
  MdRemove, MdErrorOutline, MdWarningAmber, MdInfoOutline, MdHelpOutline, MdMoreVert, MdMenu, MdOpenInNew,
  MdOutlineFileDownload, MdOutlineFileUpload, MdOutlineCalendarMonth, MdOutlinePerson, MdOutlineKeyboardDoubleArrowLeft,
  MdOutlineKeyboardDoubleArrowRight, MdOutlineReplay, MdLink, MdOutlineSnooze, MdRefresh, MdOutlinePublic, MdOutlineCloudOff,
  MdSearch, MdOutlineLogout, MdOutlineAccountCircle,
} from "react-icons/md";
import type { AgentKey } from "./tokens.js";
import { S, type Msg } from "./strings.js";

// ── Module concepts (PLAN2 §4.5, in table order) ──
export const IconDealMap = MdOutlineMap;
export const IconDeals = MdOutlineHandshake;
export const IconQuickDeal = MdOutlineBolt;
export const IconReader = MdOutlineDescription;
export const IconFulfil = MdOutlineViewKanban;
export const IconProof = MdOutlineVerified;
export const IconRoom = MdOutlineMeetingRoom;
export const IconLookout = MdOutlineNotificationsActive;
export const IconReports = MdOutlineSummarize;
export const IconRequests = MdOutlineInbox;
export const IconInventory = MdOutlineInventory2;
export const IconScout = MdOutlineTravelExplore;
export const IconPackage = MdOutlineWidgets;
export const IconRenew = MdOutlineAutorenew;
export const IconTalent = MdOutlinePeopleAlt;
export const IconPipeline = MdOutlineFilterAlt;
export const IconSwitchKit = MdOutlineMoveUp;
export const IconPlaybooks = MdOutlineMenuBook;
export const IconConnect = MdOutlineHub;
export const IconAssist = MdOutlineAssistant;
export const IconClientRooms = MdOutlineMeetingRoom;
export const IconClientRoomsBadge = MdOutlineGroups;
export const IconBrandDesk = MdOutlineStorefront;
export const IconSettings = MdOutlineSettings;
export const IconCapture = MdOutlinePhotoCamera;
export const IconFindings = MdOutlineFactCheck;
export const IconProspector = MdOutlineRadar;

// ── Agent glyphs (PLAN6 §3.6; the handoff's agent row). Concept icons are MdOutline* (D6-11: no icon font). The Verifier
// and the Prospector reuse IconCheck and IconProspector; Reader, Scout and Renewer reuse their module icon. ──
export const IconAgentMover = MdOutlineDriveFileMove;
export const IconAgentPlanner = MdOutlineSchedule;
export const IconAgentWatch = MdOutlineVisibility;
export const IconAgentChecker = MdOutlineCheckBox;
export const IconAgentReporter = MdOutlineAssessment;
export const IconAgentAssistant = MdOutlineSupportAgent;
export const IconAgentPackager = MdOutlineInventory2;
export const IconProposal = MdOutlineDiamond;

// ── UI (the canvas's Unicode glyphs → Material, PLAN2 §4.4: → ↑ ↓ ▾ ⌕ 🔒) ──
export const IconArrowForward = MdArrowForward;
export const IconArrowBack = MdArrowBack;
export const IconArrowUp = MdArrowUpward;
export const IconArrowDown = MdArrowDownward;
export const IconExpand = MdExpandMore;
export const IconDropDown = MdArrowDropDown;
export const IconLock = MdLockOutline;
export const IconClose = MdClose;
export const IconCheck = MdCheck;
export const IconAdd = MdAdd;
export const IconRemove = MdRemove;
export const IconError = MdErrorOutline;
export const IconWarning = MdWarningAmber;
export const IconInfo = MdInfoOutline;
export const IconHelp = MdHelpOutline;
export const IconMore = MdMoreVert;
export const IconMenu = MdMenu;
export const IconExternal = MdOpenInNew;
export const IconDownload = MdOutlineFileDownload;
export const IconUpload = MdOutlineFileUpload;
export const IconCalendar = MdOutlineCalendarMonth;
export const IconPerson = MdOutlinePerson;
export const IconAccount = MdOutlineAccountCircle;
export const IconPanelOpen = MdOutlineKeyboardDoubleArrowLeft;
export const IconPanelClose = MdOutlineKeyboardDoubleArrowRight;
export const IconReplay = MdOutlineReplay;
export const IconLink = MdLink;
export const IconSnooze = MdOutlineSnooze;
export const IconRefresh = MdRefresh;
export const IconRegion = MdOutlinePublic;
export const IconOffline = MdOutlineCloudOff;
export const IconSearch = MdSearch;
export const IconSignOut = MdOutlineLogout;

/** One glyph per agent (PLAN6 §3.6). AgentMark renders these; the old hand-drawn square-capped paths are gone. */
export const AGENT_ICONS: Readonly<Record<AgentKey, IconType>> = {
  reader: IconReader,
  planner: IconAgentPlanner,
  watch: IconAgentWatch,
  verifier: IconCheck,
  checker: IconAgentChecker,
  reporter: IconAgentReporter,
  scout: IconScout,
  packager: IconAgentPackager,
  renewer: IconRenew,
  mover: IconAgentMover,
  assistant: IconAgentAssistant,
  prospector: IconProspector,
};

/** Entitlement module keys (PLAN2 §5.3 MODULES). */
export type EntitlementModule =
  | "core" | "reader" | "fulfil" | "proof" | "room" | "watch" | "reports" | "docs" | "inventory" | "scout" | "package"
  | "renew" | "talent" | "pipeline" | "clientrooms" | "brand" | "api" | "assist" | "switch";

export type ModuleKey =
  | "dealmap" | "deals" | "quickdeal" | "reader" | "fulfil" | "proof" | "room" | "lookout" | "reports" | "requests"
  | "inventory" | "scout" | "package" | "renew" | "talent" | "pipeline" | "switch" | "playbooks" | "connect" | "assist"
  | "clientrooms" | "branddesk" | "settings" | "capture" | "findings" | "prospector";

export interface ModuleMeta {
  key: ModuleKey;
  icon: IconType;
  /** a second, smaller glyph drawn beside the first (Client Rooms only, §4.5) */
  badgeIcon?: IconType;
  /** locale id nav.<key> */
  nav: Msg;
  /** the entitlement module that must be present for the item to show (App. K; hasModule) */
  module: EntitlementModule;
  /** the agents that work here (the site's "<agent> works here" chip; empty = none) */
  agents: readonly AgentKey[];
}

/** THE module constants. The console's rail constants and the site's modules.ts are aliases of these objects
 * (C-122); WP10 and WP11 each assert identity with `expect(RAIL.x).toBe(MODULE_META.x)`. */
export const MODULE_META: Readonly<Record<ModuleKey, ModuleMeta>> = {
  dealmap: { key: "dealmap", icon: IconDealMap, nav: S.navDealmap, module: "core", agents: [] },
  deals: { key: "deals", icon: IconDeals, nav: S.navDeals, module: "core", agents: [] },
  quickdeal: { key: "quickdeal", icon: IconQuickDeal, nav: S.navQuickdeal, module: "core", agents: [] },
  reader: { key: "reader", icon: IconReader, nav: S.navReader, module: "reader", agents: ["reader"] },
  fulfil: { key: "fulfil", icon: IconFulfil, nav: S.navFulfil, module: "fulfil", agents: ["planner"] },
  proof: { key: "proof", icon: IconProof, nav: S.navProof, module: "proof", agents: ["verifier", "checker"] },
  room: { key: "room", icon: IconRoom, nav: S.navRoom, module: "room", agents: ["assistant"] },
  lookout: { key: "lookout", icon: IconLookout, nav: S.navLookout, module: "watch", agents: ["watch"] },
  reports: { key: "reports", icon: IconReports, nav: S.navReports, module: "reports", agents: ["reporter"] },
  requests: { key: "requests", icon: IconRequests, nav: S.navRequests, module: "core", agents: [] },
  inventory: { key: "inventory", icon: IconInventory, nav: S.navInventory, module: "inventory", agents: [] },
  scout: { key: "scout", icon: IconScout, nav: S.navScout, module: "scout", agents: ["scout"] },
  package: { key: "package", icon: IconPackage, nav: S.navPackage, module: "package", agents: ["packager"] },
  renew: { key: "renew", icon: IconRenew, nav: S.navRenew, module: "renew", agents: ["renewer"] },
  talent: { key: "talent", icon: IconTalent, nav: S.navTalent, module: "talent", agents: [] },
  pipeline: { key: "pipeline", icon: IconPipeline, nav: S.navPipeline, module: "pipeline", agents: [] },
  switch: { key: "switch", icon: IconSwitchKit, nav: S.navSwitch, module: "switch", agents: ["mover"] },
  playbooks: { key: "playbooks", icon: IconPlaybooks, nav: S.navPlaybooks, module: "core", agents: [] },
  connect: { key: "connect", icon: IconConnect, nav: S.navConnect, module: "core", agents: [] },
  assist: { key: "assist", icon: IconAssist, nav: S.navAssist, module: "assist", agents: ["assistant"] },
  clientrooms: { key: "clientrooms", icon: IconClientRooms, badgeIcon: IconClientRoomsBadge, nav: S.navClientrooms, module: "clientrooms", agents: [] },
  branddesk: { key: "branddesk", icon: IconBrandDesk, nav: S.navBranddesk, module: "brand", agents: [] },
  settings: { key: "settings", icon: IconSettings, nav: S.navSettings, module: "core", agents: [] },
  capture: { key: "capture", icon: IconCapture, nav: S.navCapture, module: "proof", agents: [] },
  findings: { key: "findings", icon: IconFindings, nav: S.navFindings, module: "core", agents: [] },
  prospector: { key: "prospector", icon: IconProspector, nav: S.navProspector, module: "pipeline", agents: ["prospector"] },
};

/** Throws naming the first key whose icon or nav id differs from MODULE_META — WP10/WP11 call it in their alias tests. */
export function assertModuleAliases(record: Partial<Record<ModuleKey, Pick<ModuleMeta, "icon" | "nav">>>): void {
  for (const [key, meta] of Object.entries(record) as Array<[ModuleKey, Pick<ModuleMeta, "icon" | "nav">]>) {
    const canonical = MODULE_META[key];
    if (!canonical) throw new Error(`assertModuleAliases: unknown module key ${key}`);
    if (meta.icon !== canonical.icon) throw new Error(`assertModuleAliases: ${key} uses a different icon (C-122)`);
    if (meta.nav[0] !== canonical.nav[0] || meta.nav[1] !== canonical.nav[1]) throw new Error(`assertModuleAliases: ${key} uses a different name id (C-122)`);
  }
}
